import { ArrowRight, Users } from 'lucide-react';
import { BookCover } from './BookCover';
import { cn } from '@/lib/cn';
import type { BookView } from '@/types/api';

/**
 * EditorialCollectionCard — the "Staff Pick Collection" row on the Library
 * page, matching docs/DESIGN.md Artboard 1 §"Editorial Collection Card".
 *
 * Full-bleed dark card with a two-column layout: curator-authored copy on
 * the left, a fanned stack of four book covers on the right. Clicking the
 * CTA routes to `/catalog?...` via `onOpen` — the Library page owns the
 * target URL so this component doesn't need to know about filtering.
 *
 * Books up to four are rendered in the stack; fewer than four degrades
 * gracefully (the remaining slots just aren't painted).
 */
export interface EditorialCollectionCardProps {
  kicker: string;
  title: string;
  description: string;
  curatedBy: string;
  ctaLabel: string;
  onOpen: () => void;
  books?: BookView[];
  className?: string;
}

export function EditorialCollectionCard({
  kicker,
  title,
  description,
  curatedBy,
  ctaLabel,
  onOpen,
  books = [],
  className,
}: EditorialCollectionCardProps) {
  const stack = books.slice(0, 4);

  return (
    <section
      className={cn(
        'relative overflow-hidden rounded-[24px] bg-or-slate-900 text-white shadow-or-pop',
        className,
      )}
    >
      {/* Radial glow — adds the warm gradient called out in the design doc
          without hitting a real image, so the card stays fast to paint. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-70"
        style={{
          background:
            'radial-gradient(circle at 15% 20%, rgba(255,169,87,0.25), transparent 55%), radial-gradient(circle at 85% 80%, rgba(90,140,255,0.18), transparent 60%)',
        }}
      />

      <div className="relative grid gap-6 p-8 md:grid-cols-[1.1fr_1fr] md:items-center md:gap-10">
        {/* --- Left column: kicker + copy + CTA --- */}
        <div className="flex flex-col gap-4">
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-or-yellow-400">
            {kicker}
          </span>
          {/* `text-white!` — the global `h1..h4 { color: ... }` rule in
              styles/index.css is unlayered, so it beats Tailwind's
              `@layer utilities` rules. The Tailwind v4 important
              modifier (`text-white!`) forces this h2 back to white. */}
          <h2 className="font-display text-[32px] font-bold leading-tight text-white!">{title}</h2>
          <p className="max-w-md text-[14px] leading-relaxed text-white/80">{description}</p>
          <div className="flex items-center gap-2 text-[12px] text-white/70">
            <span className="inline-flex items-center justify-center rounded-full bg-white/10 p-1.5">
              <Users className="h-3.5 w-3.5" aria-hidden />
            </span>
            <span>{curatedBy}</span>
          </div>
          <button
            type="button"
            onClick={onOpen}
            className="mt-2 inline-flex w-fit items-center gap-1.5 rounded-full bg-white px-5 py-2.5 text-[13px] font-semibold text-or-orange-700 shadow-or-card transition hover:bg-or-cream-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-or-orange-300/60"
          >
            {ctaLabel}
            <ArrowRight className="h-4 w-4" aria-hidden />
          </button>
        </div>

        {/* --- Right column: fanned covers --- */}
        <div className="relative hidden h-[260px] md:block" aria-hidden>
          {stack.length === 0 ? (
            <FannedPlaceholders />
          ) : (
            stack.map((book, i) => (
              <div
                key={book.id}
                className="absolute left-1/2 top-1/2 transition"
                style={{
                  // Stagger each cover around the centre so the group reads
                  // as a fan rather than a stack.
                  transform: `translate(-50%, -50%) translateX(${(i - (stack.length - 1) / 2) * 44}px) translateY(${(i % 2 === 0 ? -1 : 1) * 6}px) rotate(${(i - (stack.length - 1) / 2) * 8}deg)`,
                  zIndex: i,
                }}
              >
                <BookCover
                  title={book.title}
                  author={book.author}
                  src={book.bookCover}
                  seed={book.id}
                  w={140}
                  h={210}
                  className="rounded-xl shadow-or-pop ring-1 ring-white/10"
                />
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * Four placeholder rectangles that match the stack's positioning — shown
 * while the owning page is still waiting for its books to fetch. Dashed
 * borders + muted fill read as "loading" without needing a separate
 * skeleton component.
 */
function FannedPlaceholders() {
  return (
    <>
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="absolute left-1/2 top-1/2 h-[210px] w-[140px] rounded-xl border border-dashed border-white/20 bg-white/5"
          style={{
            transform: `translate(-50%, -50%) translateX(${(i - 1.5) * 44}px) translateY(${(i % 2 === 0 ? -1 : 1) * 6}px) rotate(${(i - 1.5) * 8}deg)`,
            zIndex: i,
          }}
        />
      ))}
    </>
  );
}
